import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Cpu, Check, FileText, MessageSquare, Download } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import LeadForm from "@/components/common/LeadForm";
import { MACHINES } from "@/data/content";

const MachineDetail = () => {
  const { id } = useParams();
  const machine = MACHINES.find((m) => m.id === id) || MACHINES[0];
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("enquiry");

  return (
    <div className="pt-28 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Link to="/machines" className="inline-flex items-center gap-2 text-sm text-dim hover:text-slate-900 transition-colors mb-8" data-testid="back-machines"><ArrowLeft className="w-4 h-4" /> Back to Machines</Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <Reveal className="rounded-2xl overflow-hidden border border-slate-200 h-[440px]">
              <motion.img key={active} initial={{ opacity: 0.4, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                src={machine.gallery[active]} alt={machine.name} className="w-full h-full object-cover" data-testid="machine-main-image" />
            </Reveal>
            {machine.gallery.length > 1 && (
              <div className="mt-4 flex gap-3">
                {machine.gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`w-24 h-20 rounded-xl overflow-hidden border transition-all ${active === i ? "border-[#0055FF]" : "border-slate-200 opacity-60 hover:opacity-100"}`} data-testid={`thumb-${i}`}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full text-xs glass border-slate-300">{machine.condition}</span>
              <span className="px-3 py-1 rounded-full text-xs bg-[#0055FF] text-white">{machine.controller}</span>
            </div>
            <span className="mt-5 block text-xs uppercase tracking-widest text-dim">{machine.type}</span>
            <h1 className="mt-2 text-3xl md:text-4xl">{machine.name}</h1>
            <div className="mt-4 flex items-center gap-2 text-slate-700"><Cpu className="w-5 h-5 text-[#0055FF]" /> Controller: {machine.controller}</div>

            <Link to={`/machines/${machine.id}/brochure`} className="btn-ghost mt-6 !py-2.5 !px-5 !text-sm" data-testid="download-brochure-btn">
              <Download className="w-4 h-4" /> Download Brochure
            </Link>

            <div className="mt-8">
              <h3 className="text-lg mb-4">Specifications</h3>
              <div className="grid grid-cols-2 gap-px bg-white/10 rounded-xl overflow-hidden border border-slate-200">
                {Object.entries(machine.specs).map(([k, v]) => (
                  <div key={k} className="bg-white p-4">
                    <p className="text-xs text-dim">{k}</p>
                    <p className="mt-1 text-sm font-medium">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-lg mb-4">Key Features</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {machine.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-[#0055FF]" /> {f}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Enquiry / Quote */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12 border-t border-slate-200 pt-16">
          <div>
            <span className="eyebrow">Interested in this machine?</span>
            <h2 className="mt-5 text-3xl md:text-4xl">Request details or a quotation</h2>
            <p className="mt-4 text-dim">Our engineers will respond with availability, pricing and full documentation for <span className="text-slate-900 font-medium">{machine.name}</span>.</p>
            <div className="mt-8 flex gap-3">
              <button onClick={() => setTab("enquiry")} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border ${tab === "enquiry" ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-slate-200 text-slate-600"}`} data-testid="tab-enquiry"><MessageSquare className="w-4 h-4" /> Enquiry</button>
              <button onClick={() => setTab("quote")} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border ${tab === "quote" ? "bg-[#0055FF] border-[#0055FF]" : "border-slate-200 text-slate-600"}`} data-testid="tab-quote"><FileText className="w-4 h-4" /> Quote</button>
            </div>
          </div>
          <div className="bg-white border border-[#0055FF]/30 rounded-2xl p-8">
            <LeadForm kind={tab} presetMachine={machine.name} presetService={machine.name} compact />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineDetail;
